// Resolve static-group members from a parsed CSV of client names.
//
// Client UUIDs are not surfaced to staff anywhere in the product, so the import
// matches on name: a header row with `first_name` and `last_name` columns, one
// client per row. Matching is done against the location's own client list
// (`allClients` is already scoped to the current business location), so a match
// can only ever be a client of this location. A name that maps to more than one
// client (duplicate names) resolves to ALL of those clients. A row that matches
// no client is returned in `unmatched`.

interface NamedClient {
  id: string;
  first_name?: string;
  last_name?: string;
}

export interface NameMatchResult {
  /** Unique client ids matched (deduped; includes every client of a duplicate name). */
  matchedIds: string[];
  /** Display names from rows that matched no client of this location. */
  unmatched: string[];
}

const norm = (s: string) => (s ?? "").toLowerCase().trim().replace(/\s+/g, " ");

// Accept `first_name` / `first name` / `first` (and the last-name equivalents),
// case-insensitive. No "use the first column" fallback — the header must name
// the columns.
function pickCol(headers: string[], names: string[]): string | null {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const n of names) {
    const i = lower.indexOf(n);
    if (i !== -1) return headers[i];
  }
  return null;
}

export function matchClientsByName(
  headers: string[],
  records: Record<string, string>[],
  clients: NamedClient[],
): NameMatchResult {
  const firstCol = pickCol(headers, ["first_name", "first name", "first"]);
  const lastCol = pickCol(headers, ["last_name", "last name", "last"]);

  // Index the location's clients by normalized "first last".
  const byName = new Map<string, string[]>();
  for (const c of clients || []) {
    const key = `${norm(c.first_name ?? "")} ${norm(c.last_name ?? "")}`.trim();
    if (!key) continue;
    const arr = byName.get(key);
    if (arr) arr.push(c.id);
    else byName.set(key, [c.id]);
  }

  const matched = new Set<string>();
  const unmatched: string[] = [];
  for (const r of records) {
    const first = firstCol ? (r[firstCol] ?? "") : "";
    const last = lastCol ? (r[lastCol] ?? "") : "";
    const display = `${first} ${last}`.trim();
    if (!display) continue; // blank row
    const key = `${norm(first)} ${norm(last)}`.trim();
    const ids = byName.get(key);
    if (ids && ids.length) {
      ids.forEach((id) => matched.add(id)); // duplicate names → add all
    } else {
      unmatched.push(display);
    }
  }
  return { matchedIds: Array.from(matched), unmatched };
}
